import { Users, Briefcase, Zap, Info, Brain } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { motion, AnimatePresence } from 'framer-motion';

export type TeamType = 'customer-support' | 'software-dev' | 'research-academic' | 'general-knowledge';
export type ResponseSpeed = 'fast' | 'balanced' | 'relaxed';
export type KnowledgeScope = 'focused' | 'broad' | 'expert';

interface QuickStartCardProps {
  userCount: number;
  teamType: TeamType;
  responseSpeed: ResponseSpeed;
  knowledgeScope: KnowledgeScope;
  onUserCountChange: (count: number) => void;
  onTeamTypeChange: (teamType: TeamType) => void;
  onSpeedChange: (speed: ResponseSpeed) => void;
  onKnowledgeScopeChange: (scope: KnowledgeScope) => void;
  teamComposition?: Array<{ name: string; percentage: number }>;
}

const USER_PRESETS: Array<{
  value: number;
  label: string;
  desc: string;
}> = [
  { value: 100, label: '100', desc: 'Small team' },
  { value: 500, label: '500', desc: 'Department' },
  { value: 1000, label: '1,000', desc: 'Division' },
  { value: 2500, label: '2,500', desc: 'Enterprise' },
  { value: 5000, label: '5,000', desc: 'Large scale' },
];

export const TEAM_OPTIONS: Array<{
  id: TeamType;
  label: string;
  desc: string;
  templateId: string;
}> = [
  { id: 'customer-support', label: 'Customer Support', desc: 'Help desk, ticket handling', templateId: 'customer-service' },
  { id: 'software-dev', label: 'Software Development', desc: 'Coding, code review, debugging', templateId: 'software-dev' },
  { id: 'research-academic', label: 'Research & Analysis', desc: 'Deep research, document analysis', templateId: 'research-academic' },
  { id: 'general-knowledge', label: 'General Knowledge', desc: 'General-purpose assistant', templateId: 'general-knowledge' },
];

const SPEED_OPTIONS: Array<{
  id: ResponseSpeed;
  label: string;
  desc: string;
  meaning: string;
}> = [
  { id: 'fast', label: 'Fast', desc: 'Chatbot-like responsiveness', meaning: 'Low latency target, lower batching' },
  { id: 'balanced', label: 'Balanced', desc: 'Good UX, efficient resources', meaning: 'Normal chat UX targets' },
  { id: 'relaxed', label: 'Relaxed', desc: 'Batch-friendly, max efficiency', meaning: 'Higher throughput, higher latency allowed' },
];

const SCOPE_OPTIONS: Array<{
  id: KnowledgeScope;
  label: string;
  desc: string;
  modelSizeB: number;
  meaning: string;
}> = [
  { id: 'focused', label: 'Focused', desc: 'Task-specific, efficient', modelSizeB: 7, meaning: '7B model — fast, lower memory' },
  { id: 'broad', label: 'Broad', desc: 'General knowledge, versatile', modelSizeB: 70, meaning: '70B model — balanced capability' },
  { id: 'expert', label: 'Expert', desc: 'Deep reasoning, max capability', modelSizeB: 405, meaning: '405B model — highest capability' },
];

// Helper to map model size to knowledge scope
export function getScopeForModelSize(paramsB: number): KnowledgeScope {
  if (paramsB <= 20) return 'focused';
  if (paramsB <= 100) return 'broad';
  return 'expert';
}
// Shared button style helper for consistent sizing
const optionButtonClasses = (isSelected: boolean) => cn(
  'p-3 rounded-lg border-2 text-center transition-all',
  'min-h-[72px] flex flex-col justify-center', // Fixed height + centered content
  isSelected
    ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
    : 'border-border hover:border-primary/50 hover:bg-muted/50'
);

export function QuickStartCard({
  userCount,
  teamType,
  responseSpeed,
  knowledgeScope,
  onUserCountChange,
  onTeamTypeChange,
  onSpeedChange,
  onKnowledgeScopeChange,
  teamComposition
}: QuickStartCardProps) {
  // Find closest preset for "What this means" display
  const closestPreset = USER_PRESETS.reduce((prev, curr) => 
    Math.abs(curr.value - userCount) < Math.abs(prev.value - userCount) ? curr : prev
  );
  return <Card className="border-2 border-primary/20 bg-gradient-to-br from-card via-card to-primary/5 shadow-lg">
      <CardContent className="p-6 space-y-6">
        {/* Header */}
        <div className="text-center space-y-1">
          <h2 className="text-2xl font-bold text-foreground">
            Size Your Infrastructure in 60 Seconds
          </h2>
          <p className="text-muted-foreground">
            Answer 4 quick questions to get started
          </p>
        </div>

        {/* Question 1: User Count */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Users className="h-4 w-4 text-primary" />
            How many users will use this at the same time?
          </label>
          <div className="grid grid-cols-5 gap-2">
            {USER_PRESETS.map(preset => (
              <button
                key={preset.value}
                onClick={() => onUserCountChange(preset.value)}
                className={optionButtonClasses(userCount === preset.value)}
              >
                <div className="font-semibold text-sm text-foreground">
                  {preset.label}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 hidden sm:block line-clamp-1">
                  {preset.desc}
                </div>
              </button>
            ))}
          </div>
          {/* What this means */}
          <div className="text-xs text-muted-foreground text-center pt-1">
            <span className="font-medium">Simultaneous active users:</span>{' '}
            {userCount.toLocaleString()} users chatting at the same time
          </div>
        </div>

        {/* Question 2: Use Case */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Briefcase className="h-4 w-4 text-primary" />
            What will your team primarily use AI for?
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {TEAM_OPTIONS.map(option => (
              <button
                key={option.id}
                onClick={() => onTeamTypeChange(option.id)}
                className={optionButtonClasses(teamType === option.id)}
              >
                <div className="font-semibold text-sm text-foreground">
                  {option.label}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 hidden sm:block line-clamp-1">
                  {option.desc}
                </div>
              </button>
            ))}
          </div>
          {/* Team composition breakdown */}
          {teamComposition && teamComposition.length > 0 && (
            <div className="text-xs text-muted-foreground text-center pt-1">
              <span className="font-medium">Team composition:</span>{' '}
              {teamComposition.map((p, i) => (
                <span key={p.name}>
                  {p.name} ({p.percentage}%){i < teamComposition.length - 1 ? ', ' : ''}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Question 3: Knowledge Scope */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Brain className="h-4 w-4 text-primary" />
            How much model capability do you need?
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-3.5 w-3.5 text-muted-foreground/60" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <div className="space-y-1.5 text-xs">
                    <p><strong>Focused (7B):</strong> Fast responses, lower memory usage. Best for specific, well-defined tasks.</p>
                    <p><strong>Broad (70B):</strong> Balanced capability and resource usage. Handles most enterprise use cases well.</p>
                    <p><strong>Expert (405B):</strong> Maximum reasoning capability. Highest memory requirements but best for complex analysis.</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </label>
          <div className="grid grid-cols-3 gap-2">
            {SCOPE_OPTIONS.map(option => (
              <button 
                key={option.id} 
                onClick={() => onKnowledgeScopeChange(option.id)} 
                className={optionButtonClasses(knowledgeScope === option.id)}
              >
                <div className="font-semibold text-sm text-foreground">
                  {option.label}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 hidden sm:block line-clamp-1">
                  {option.desc}
                </div>
              </button>
            ))}
          </div>
          {/* What this means */}
          <div className="text-xs text-muted-foreground text-center pt-1">
            <span className="font-medium">What this means:</span>{' '}
            {SCOPE_OPTIONS.find(o => o.id === knowledgeScope)?.meaning}
          </div>
        </div>

        {/* Question 4: Response Speed */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Zap className="h-4 w-4 text-primary" />
            How important is response speed?
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="h-3.5 w-3.5 text-muted-foreground/60" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p>This setting impacts latency targets and batching assumptions.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </label>
          <div className="grid grid-cols-3 gap-2">
            {SPEED_OPTIONS.map(option => (
              <button 
                key={option.id} 
                onClick={() => onSpeedChange(option.id)} 
                className={optionButtonClasses(responseSpeed === option.id)}
              >
                <div className="font-semibold text-sm text-foreground">
                  {option.label}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 hidden sm:block line-clamp-1">
                  {option.desc}
                </div>
              </button>
            ))}
          </div>
          {/* What this means */}
          <div className="text-xs text-muted-foreground text-center pt-1">
            <span className="font-medium">What this means:</span>{' '}
            {SPEED_OPTIONS.find(o => o.id === responseSpeed)?.meaning}
          </div>
        </div>
      </CardContent>
    </Card>;
}